/** @format */

import {ReactNode, useEffect, useState} from 'react';

import {Box, Button} from '@mui/material';
import {clsx} from 'clsx';
import DateRangeIcon from '@mui/icons-material/DateRange';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import {differenceInCalendarDays} from 'date-fns';

import {ru} from 'date-fns/locale';

import DatePicker from 'react-datepicker';

import {Discreteness, Period, PeriodsModel, SwipeDirection} from './model';
import {StyledPeriodBox, periodClasses as classes, HintText} from './styles';
import 'react-datepicker/dist/react-datepicker.css';

export type DateTuple = [Date | null, Date | null];

import validateDatesRange from './helpers/validateDatesRange';
import {DISCRETENESS_LIST, FUTURE_THRESHOLD_DAYS, HINT_DELAY} from './const';
import {getDisableDiscreteness} from './helpers/getDisableDiscreteness';
import {HintPopper} from '../HintPopper';

interface PeriodSelectorProps {
  startDate: Date;
  endDate: Date;
  activePeriod: Period;
  activeDiscreteness?: Discreteness;
  onChangePeriod: (value: Period) => void;
  onChangeDiscreteness?: (value: Discreteness) => void;
  onSwipePeriod?: (value: SwipeDirection) => void;
  onDateRangeConfirm?: (startDate: Date, endDate: Date) => void;
  periodsModel: PeriodsModel[];
  canSwipe?: boolean;
  showPeriodButton?: boolean;
  allowSameDayRange?: boolean;
}

const PeriodSelector = ({
  activePeriod,
  activeDiscreteness,
  onChangePeriod,
  onChangeDiscreteness,
  startDate,
  endDate,
  onDateRangeConfirm,
  onSwipePeriod,
  periodsModel,
  canSwipe = false,
  showPeriodButton = true,
  allowSameDayRange = false,
}: PeriodSelectorProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [rangeValue, setRangeValue] = useState<DateTuple>();

  useEffect(() => {
    setRangeValue([startDate, endDate]);
    setOpen(false);
  }, [startDate, endDate]);

  const disableSwipeToFuture =
    differenceInCalendarDays(new Date(), endDate) <= FUTURE_THRESHOLD_DAYS;

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = (date: DateTuple) => {
    const range = validateDatesRange(date, allowSameDayRange);
    if (range && onDateRangeConfirm) {
      onDateRangeConfirm(range.start, range.end);
      onChangePeriod('custom');
    } else {
      setRangeValue([startDate, endDate]);
    }
  };

  const handleChange = (date: DateTuple) => {
    setRangeValue(date);
    if (date[0] && date[1]) {
      handleConfirm(date);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setRangeValue([startDate, endDate]);
    handleClose();
  };

  const showRange = open || activePeriod === 'custom';

  const renderPopperContent = (discreteness: Discreteness): ReactNode | null => {
    switch (discreteness) {
      case 'week':
        return <HintText>Задайте период больше одной недели</HintText>;
      case 'month':
        return <HintText>Задайте период больше одного месяца</HintText>;
      default:
        return null;
    }
  };

  return (
    <StyledPeriodBox data-testid='period-selector'>
      <Box className={classes.controlsBlock}>
        {periodsModel.map(period => (
          <Button
            key={period.key}
            data-testid={`period-button-${period.key}`}
            className={clsx(classes.controlButton, {
              [classes.controlButtonActive]: activePeriod === period.key,
            })}
            onClick={() => onChangePeriod(period.key)}
          >
            {period.name}
          </Button>
        ))}

        {showPeriodButton && onDateRangeConfirm && (
          <Button
            className={clsx(classes.controlButton, {
              [classes.controlButtonActive]: showRange,
            })}
            onClick={handleOpen}
            data-testid='period-range'
            disableRipple
          >
            <DateRangeIcon
              className={clsx(classes.dateButtonIcon, {
                [classes.dateButtonIconActive]: open,
              })}
            />
            {showRange ? (
              <DatePicker
                open={open}
                selectsRange
                startDate={rangeValue?.[0] || undefined}
                endDate={rangeValue?.[1] || undefined}
                portalId='PeriodSelectorDateRange'
                dateFormat='dd.MM.yyyy'
                locale={ru}
                onChange={handleChange}
                onClickOutside={handleCancel}
                maxDate={new Date()}
                placeholderText='Период'
              />
            ) : (
              'Период'
            )}
          </Button>
        )}
      </Box>

      {activeDiscreteness && onChangeDiscreteness && (
        <Box className={classes.controlsBlock}>
          {DISCRETENESS_LIST.map(discreteness => {
            const disabled = getDisableDiscreteness({
              currentKey: discreteness.key,
              startDate,
              endDate,
            });

            const PopperContent = disabled && renderPopperContent(discreteness.key);
            const isActive = activeDiscreteness === discreteness.key;

            return (
              <HintPopper key={discreteness.key} PopperContent={PopperContent} delay={HINT_DELAY}>
                <Button
                  data-testid={`discreteness-button-${discreteness.key}`}
                  className={clsx(classes.controlButton, {
                    [classes.controlButtonActive]: activeDiscreteness === discreteness.key,
                    [classes.controlButtonDisabled]: disabled,
                  })}
                  onClick={() => {
                    if (!isActive) {
                      onChangeDiscreteness(discreteness.key);
                    }
                  }}
                  disabled={disabled}
                >
                  {discreteness.name}
                </Button>
              </HintPopper>
            );
          })}
        </Box>
      )}

      {canSwipe && onSwipePeriod && (
        <Box className={classes.binaryControlsBlock} data-testid='period-swiper'>
          <Button
            className={classes.swipeButton}
            onClick={() => onSwipePeriod(-1)}
            data-testid='period-swipe-left'
          >
            <ArrowBackIosNewIcon />
          </Button>
          <Button
            className={clsx(classes.swipeButton, {
              [classes.swipeButtonDisabled]: disableSwipeToFuture,
            })}
            onClick={() => onSwipePeriod(1)}
            disabled={disableSwipeToFuture}
            data-testid='period-swipe-right'
          >
            <ArrowBackIosNewIcon />
          </Button>
        </Box>
      )}
    </StyledPeriodBox>
  );
};

export {PeriodSelector};
